import { Component, EventEmitter, HostListener, Input, Output, computed, signal } from '@angular/core';
import { CustomModalComponent } from '@/components/custom-modal/custom-modal.component';

/** Billetes que se ofrecen como atajo; cada pulsación suma al importe entregado. */
const QUICK_BILLS = [5, 10, 20, 50, 100];

/**
 * Calculadora de cambio para cobros en efectivo: el cajero teclea (o suma con los billetes rápidos)
 * lo que entrega el cliente y ve el cambio a devolver antes de confirmar el ticket.
 * Todos los importes en euros, no céntimos.
 */
@Component({
  selector: 'change-calculator',
  imports: [CustomModalComponent],
  templateUrl: './change-calculator.component.html',
})
export class ChangeCalculatorComponent {
  @Input() set amountDue(value: number | string) {
    this.due.set(Math.round(Number(value || 0) * 100) / 100);
  }
  @Input() confirmLabel = 'Cobrar';
  @Output() closeFunction = new EventEmitter<void>();
  @Output() acceptFunction = new EventEmitter<{ given: number | null, change: number | null }>();

  public quickBills = QUICK_BILLS;
  public keys = ['7', '8', '9', '4', '5', '6', '1', '2', '3', ',', '0', '⌫'];

  public due = signal(0);
  /** Lo tecleado tal cual (con coma decimal), vacío = todavía no se ha introducido nada. */
  public entered = signal('');

  public given = computed(() => {
    const raw = this.entered();
    return raw === '' ? null : Number(raw.replace(',', '.')) || 0;
  });

  public change = computed(() => {
    const given = this.given();
    return given === null ? null : Math.round((given - this.due()) * 100) / 100;
  });

  /** Sin importe introducido se permite cobrar igualmente (pago exacto / no se quiere calcular). */
  public canConfirm = computed(() => this.change() === null || this.change()! >= 0);

  pressKey(key: string) {
    const current = this.entered();

    if (key === '⌫') {
      this.entered.set(current.slice(0, -1));
      return;
    }

    if (key === ',') {
      if (!current.includes(',')) this.entered.set((current || '0') + ',');
      return;
    }

    // máx. 2 decimales
    const decimals = current.split(',')[1];
    if (decimals !== undefined && decimals.length >= 2) return;

    this.entered.set(current === '0' ? key : current + key);
  }

  addBill(amount: number) {
    this.setAmount((this.given() ?? 0) + amount);
  }

  setExact() {
    this.setAmount(this.due());
  }

  clear() {
    this.entered.set('');
  }

  private setAmount(amount: number) {
    const rounded = Math.round(amount * 100) / 100;
    this.entered.set(Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace('.', ','));
  }

  confirm() {
    if (!this.canConfirm()) return;
    this.acceptFunction.emit({ given: this.given(), change: this.change() });
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (/^[0-9]$/.test(event.key)) {
      this.pressKey(event.key);
    } else if (event.key === ',' || event.key === '.') {
      this.pressKey(',');
    } else if (event.key === 'Backspace') {
      this.pressKey('⌫');
    } else if (event.key === 'Delete') {
      this.clear();
    } else if (event.key === 'Enter') {
      this.confirm();
    } else if (event.key === 'Escape') {
      this.closeFunction.emit();
    } else {
      return;
    }
    event.preventDefault();
  }

  format(value: number): string {
    return value.toFixed(2).replace('.', ',');
  }
}
